<template>
  <transition name="modal">
    <div v-if="isOpen" class="fixed inset-0 z-[60]">
      <div
        class="modal-backdrop absolute inset-0 bg-gray-900/60"
        @click="handleBackdropClick"
      ></div>
      <div
        class="relative flex min-h-full items-center justify-center overflow-y-auto px-4 py-6 sm:px-6 pointer-events-none"
      >
        <div :class="['relative w-full pointer-events-auto', maxWidthClass]">
          <div
            class="relative overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200/50"
          >
            <!-- Close button -->
            <button
              v-if="showCloseButton"
              class="absolute right-4 top-4 z-10 rounded-lg p-1.5 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
              type="button"
              @click.stop="handleClose"
            >
              <span class="sr-only">Close</span>
              <svg
                class="h-5 w-5"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <!-- Modal content slot -->
            <div :class="contentPaddingClass">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "BaseModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    maxWidth: {
      type: String,
      default: "md",
      validator: (value) =>
        ["sm", "md", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "full"].includes(
          value
        ),
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    contentPadding: {
      type: String,
      default: "default",
      validator: (value) =>
        ["none", "small", "default", "large"].includes(value),
    },
  },
  computed: {
    maxWidthClass() {
      const widthMap = {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
        "5xl": "max-w-5xl",
        full: "max-w-full",
      };
      return widthMap[this.maxWidth] || widthMap.md;
    },
    contentPaddingClass() {
      const paddingMap = {
        none: "",
        small: "px-4 pb-4 pt-6 sm:px-6 sm:pt-6",
        default: "px-6 pb-6 pt-8 sm:px-8 sm:pb-8 sm:pt-10",
        large: "px-8 pb-8 pt-10 sm:px-10 sm:pb-10 sm:pt-12",
      };
      return paddingMap[this.contentPadding] || paddingMap.default;
    },
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop) {
        this.handleClose();
      }
    },
  },
};
</script>

<style scoped>
.modal-enter-active {
  transition: opacity 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease-in;
}

.modal-enter-active .relative.w-full > div {
  animation: modalSlideIn 0.3s ease-out;
}

.modal-leave-active .relative.w-full > div {
  animation: modalSlideOut 0.2s ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative.w-full > div,
.modal-leave-to .relative.w-full > div {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

/* Backdrop blur animation */
.modal-backdrop {
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  will-change: backdrop-filter, opacity;
}

.modal-enter-from .modal-backdrop {
  opacity: 0;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

.modal-enter-active .modal-backdrop {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    backdrop-filter 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    -webkit-backdrop-filter 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-leave-from .modal-backdrop {
  opacity: 1;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-leave-active .modal-backdrop {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 1, 1),
    backdrop-filter 0.2s cubic-bezier(0.4, 0, 1, 1),
    -webkit-backdrop-filter 0.2s cubic-bezier(0.4, 0, 1, 1);
  opacity: 0;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes modalSlideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
}
</style>
